import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import Link from '../../../Router/Link';
import Container from '@styled/Container';
import Distance from '@styled/Distance';
import { H1, H2, H3, H4, H5 } from '@styled/Heading';
import OverflowSlider from '@styled/OverflowSlider';
import {
  Drilldown,
  DrilldownItem,
  ItemImage,
  ItemLabel,
  ItemNoImage,
} from '@styled/Drilldown/wrappers';
import { useTrackGenericEvent } from '../../../hooks/tracking/useTrackingEvents';

export interface INavigationSlice {
  heading?: string;
  headingLevel?: string;
  titleHeadingLevel?: 1;
  italicTitle?: boolean;
  boldTitle?: boolean;
  id?: string;
  position?: string;
  items: Array<{
    imageUrl?: string;
    title: string;
    url: string;
  }>;
}

const HeadingComponent = ({
  level,
  semanticHeadingLevel,
  ...rest
}: {
  level?: string;
  semanticHeadingLevel: string;
  children: React.ReactNode;
  className?: string;
}) => {
  switch (level) {
    case 'h2':
      return <H2 as={semanticHeadingLevel} {...rest} />;

    case 'h3':
      return <H3 as={semanticHeadingLevel} {...rest} />;

    case 'h4':
      return <H4 as={semanticHeadingLevel} {...rest} />;

    case 'h5':
      return <H5 as={semanticHeadingLevel} {...rest} />;

    default:
      return <H1 as={semanticHeadingLevel} {...rest} />;
  }
};

const NavigationSlice = ({
  heading,
  headingLevel,
  italicTitle: italic,
  boldTitle: bold,
  id,
  items,
  position,
  titleHeadingLevel,
}: INavigationSlice) => {
  const scrollEvent = useMemo(
    () => ({
      event: 'navigation_slice_scroll',
      value: id,
    }),
    [id],
  );

  const trackScroll = useTrackGenericEvent(scrollEvent);

  if (!items?.length) {
    return null;
  }

  return (
    <Container>
      {heading && (
        <>
          <HeadingComponent
            level={headingLevel}
            semanticHeadingLevel={titleHeadingLevel === 1 || headingLevel === 'h1' ? 'h1' : 'h2'}
            className={clsx(
              clsx({
                'text-center': position === 'center',
                italic,
                bold,
              }),
            )}>
            {heading}
          </HeadingComponent>

          <Distance size="2" />
        </>
      )}

      <OverflowSlider
        stepSize={1 / 2}
        drilldown
        centered={position === 'center'}
        onScrollOnce={trackScroll}>
        <Drilldown>
          {items.map((item, i) => (
            <Item {...item} key={item.url} imageLoading={i > 3 ? 'lazy' : undefined} />
          ))}
        </Drilldown>
      </OverflowSlider>
    </Container>
  );
};

export default NavigationSlice;

interface IItem {
  imageLoading?: 'eager' | 'lazy';
  imageUrl?: string;
  title: string;
  url: string;
}

const Item = ({ imageLoading, url, title, imageUrl }: IItem) => {
  const clickEvent = useMemo(
    () => ({
      event: 'navigation_slice_click',
      value: title,
    }),
    [title],
  );

  const trackClick = useTrackGenericEvent(clickEvent);

  const onClick = useCallback(() => {
    trackClick();
  }, [trackClick]);

  return (
    <Link Component={DrilldownItem} href={url} tone="transparent" onBeforeNavigate={onClick}>
      {imageUrl ? (
        <picture>
          <source
            srcSet={`${imageUrl}?width=120&height=120&crop=false&output=webp 1x, ${imageUrl}?width=144&height=144&crop=false&output=webp 2x`}
            media="(min-width: 900px)"
            type="image/webp"
          />
          <source
            srcSet={`${imageUrl}?width=120&height=120&crop=false`}
            media="(min-width: 900px)"
            type="image/jpeg"
          />
          <source
            srcSet={`${imageUrl}?width=72&height=72&crop=false&output=webp, ${imageUrl}?width=144&height=144&crop=false&output=webp 2x`}
            media="(max-width: 899px)"
            type="image/webp"
          />
          <ItemImage
            src={`${imageUrl}?width=72&height=72&crop=false`}
            srcSet={`${imageUrl}?width=72&height=72&crop=false, ${imageUrl}?width=144&height=144&crop=false 2x`}
            loading={imageLoading}
          />
        </picture>
      ) : (
        <ItemNoImage />
      )}
      <ItemLabel>{title}</ItemLabel>
    </Link>
  );
};
